import {
  ContextMenu,
  isTyping,
  itemForShortcut,
  type MenuAnchor,
  type MenuHeading,
  type MenuItem,
} from "@rhema/ui";
import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import { useIsDesktop } from "../app/media";
import { hapticTick } from "../platform/haptic";

const HOLD_MS = 450;

/** Whether an event came from the open menu, which is drawn inside the row it belongs to. */
function inMenu(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return Boolean(el && typeof el.closest === "function" && el.closest("[data-menu-layer]"));
}

export type ItemMenu = {
  /** Spread on the row: right-click, long press, and the context menu key or Shift and F10. */
  bind: {
    onContextMenu: (e: MouseEvent) => void;
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onPointerLeave: () => void;
    onKeyDown: (e: KeyboardEvent) => void;
    onClickCapture: (e: MouseEvent) => void;
  };
  /** Opens the menu under a "…" button. */
  openFrom: (el: HTMLElement) => void;
  /** Whether the menu is open, for the "…" button's aria-expanded. */
  open: boolean;
  /** The menu itself, rendered once next to the row. */
  menu: ReactNode;
};

/**
 * One item's actions, opened the same ways everywhere: right-click on desktop, a long press on
 * phones, the context menu key, and the "…" button. `items` is read when the menu opens, so it is
 * always current. `heading` titles the phone sheet with what the menu acts on.
 */
export function useItemMenu(label: string, items: () => MenuItem[], heading?: MenuHeading): ItemMenu {
  const desktop = useIsDesktop();
  const [anchor, setAnchor] = useState<MenuAnchor | null>(null);
  const [shown, setShown] = useState<MenuItem[]>([]);
  const opener = useRef<HTMLElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const start = useRef<{ x: number; y: number } | null>(null);
  // A long press opens the menu, and the click that follows it must not also open the row.
  const swallowClick = useRef(false);
  // Whether the current press already opened the menu, so the phone's own context menu event adds nothing.
  const held = useRef(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const open = (at: MenuAnchor, from: HTMLElement | null) => {
    const list = items();
    if (!list.length) return;
    opener.current = from;
    setShown(list);
    setAnchor(at);
  };
  const cancel = () => {
    window.clearTimeout(timer.current);
    start.current = null;
  };

  return {
    bind: {
      onContextMenu: (e) => {
        e.preventDefault();
        // The open menu sits inside the row. A right-click on it is not a new request.
        if (inMenu(e.target)) return;
        if (held.current) return;
        cancel();
        open({ x: e.clientX, y: e.clientY }, e.currentTarget as HTMLElement);
      },
      onPointerDown: (e) => {
        // A new press starts afresh, so a long press with no click after it never eats the next one.
        swallowClick.current = false;
        if (e.pointerType === "mouse" || inMenu(e.target)) return;
        held.current = false;
        start.current = { x: e.clientX, y: e.clientY };
        const at = { x: e.clientX, y: e.clientY };
        const row = e.currentTarget as HTMLElement;
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => {
          swallowClick.current = true;
          held.current = true;
          hapticTick();
          open(at, row);
        }, HOLD_MS);
      },
      onPointerMove: (e) => {
        if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 8)
          cancel();
      },
      onPointerUp: cancel,
      onPointerCancel: cancel,
      onPointerLeave: cancel,
      onKeyDown: (e) => {
        if (inMenu(e.target)) return;
        if (e.key === "ContextMenu" || (e.key === "F10" && e.shiftKey)) {
          e.preventDefault();
          const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
          // Opened from the keyboard, the menu sits under the row.
          open({ x: box.left + 24, y: box.bottom }, e.target as HTMLElement);
          return;
        }
        // The shortcuts shown in the menu work on the focused row without opening it. Keys typed
        // into a field inside the row stay with the field.
        if (anchor || isTyping(e.target)) return;
        const hit = itemForShortcut(items(), e.nativeEvent);
        if (!hit || hit.type === "submenu") return;
        e.preventDefault();
        e.stopPropagation();
        hit.onSelect();
      },
      onClickCapture: (e) => {
        if (!swallowClick.current || inMenu(e.target)) return;
        swallowClick.current = false;
        e.preventDefault();
        e.stopPropagation();
      },
    },
    openFrom: (el) => {
      const box = el.getBoundingClientRect();
      // Right-aligned under the button. The menu moves left as far as it needs to stay on screen.
      open({ x: box.right - 232, y: box.bottom + 4 }, el);
    },
    open: anchor !== null,
    menu: (
      <ContextMenu
        label={label}
        items={shown}
        anchor={anchor}
        sheet={!desktop}
        heading={heading}
        opener={opener.current}
        onClose={() => setAnchor(null)}
      />
    ),
  };
}
